import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  Cycle,
  EASE,
  KnotState,
  S,
  cycleLength,
  knotGeometry,
  prefersReducedMotion,
  stateAt,
  tieCycle,
  untieCycle,
} from "../../lib/knot";

// The knot is drawn at half the logo's size.
const SCALE = 0.5;

/**
 * A cord along the nav's edge with a knot under the tab you're on. Going to
 * another tab loosens the knot, slides it along and pulls it tight there;
 * a page off the tabs unties it, and coming back ties it again.
 */
const NavCord: React.FC<{
  /** What holds the tabs: the knot sits under the active link in it. */
  tabs: React.RefObject<HTMLElement | null>;
  className?: string;
}> = ({ tabs, className }) => {
  const svg = useRef<SVGSVGElement>(null);
  const { pathname } = useLocation();
  // Where the knot is along the cord (px), and its state.
  const x = useRef(0);
  const st = useRef<KnotState>(S(0, 0));
  const raf = useRef(0);

  const paint = useCallback(() => {
    const el = svg.current!;
    const W = el.clientWidth;
    el.setAttribute("viewBox", `0 0 ${W} 20`);
    const g = knotGeometry({
      ...st.current,
      cx: x.current / SCALE,
      cy: 0,
      x0: -40,
      x1: W / SCALE + 40,
    });
    el.querySelector(".knot-cord")!.setAttribute("d", g.cord);
    const cases = el.querySelectorAll(".knot-case");
    const turns = el.querySelectorAll<SVGPathElement>(".knot-turn");
    g.turns.forEach((t, i) => {
      for (const path of [cases[i], turns[i]] as SVGPathElement[]) {
        path.setAttribute("d", t.d);
        path.style.display = t.f > 0.001 ? "" : "none";
        path.setAttribute("stroke-dasharray", t.f < 0.999 ? `${t.f} 2` : "");
      }
    });
  }, []);

  // Where the active tab's middle is along the cord, or null off the tabs.
  const target = useCallback(() => {
    const tab = tabs.current?.querySelector("a.active");
    if (!tab) return null;
    const r = tab.getBoundingClientRect();
    return r.left + r.width / 2 - svg.current!.getBoundingClientRect().left;
  }, [tabs]);

  // Run `legs` (each: how long, and the state and place at each moment).
  const run = useCallback(
    (legs: [number, (t: number) => void][]) => {
      cancelAnimationFrame(raf.current);
      let start: number | null = null;
      const tick = (now: number) => {
        start ??= now;
        let t = now - start;
        for (const [ms, at] of legs) {
          if (t < ms) {
            at(t / ms);
            paint();
            raf.current = requestAnimationFrame(tick);
            return;
          }
          t -= ms;
        }
        legs[legs.length - 1][1](1);
        paint();
      };
      raf.current = requestAnimationFrame(tick);
    },
    [paint],
  );

  const cycle = useCallback(
    (c: Cycle): [number, (t: number) => void] => [
      cycleLength(c),
      (t) => {
        st.current = stateAt(t * cycleLength(c), c);
      },
    ],
    [],
  );

  // After layout, not during it: the tabs' ref may belong to a parent,
  // which React attaches only after its children's layout effects.
  useEffect(() => {
    const to = target();
    const tied = st.current.p > 0.5;
    if (prefersReducedMotion() || (!tied && to === null)) {
      if (to !== null) x.current = to;
      st.current = S(to === null ? 0 : 3, 1);
      paint();
      return;
    }
    if (to === null) run([cycle(untieCycle(3, st.current))]);
    else if (!tied) {
      x.current = to;
      run([cycle(tieCycle(3, st.current))]);
    } else {
      const from = x.current;
      if (Math.abs(to - from) < 0.5) return;
      // Loosen, slide along, pull tight.
      const c0 = st.current.c;
      run([
        [
          170,
          (t) => {
            st.current = S(3, c0 - (c0 - 0.1) * EASE.inOut(t));
          },
        ],
        [
          420,
          (t) => {
            x.current = from + (to - from) * EASE.inOut(t);
          },
        ],
        [
          520,
          (t) => {
            x.current = to;
            st.current = S(3, 0.1 + 0.9 * EASE.cinch(t));
          },
        ],
      ]);
    }
  }, [pathname, target, paint, run, cycle]);

  // A new width moves the tabs: the knot goes straight to its tab.
  useEffect(() => {
    const onResize = () => {
      const to = target();
      if (to !== null) x.current = to;
      paint();
    };
    let live = true;
    window.addEventListener("resize", onResize);
    // Web fonts change the tabs' widths.
    document.fonts?.ready.then(() => live && onResize());
    return () => {
      live = false;
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf.current);
    };
  }, [target, paint]);

  return (
    <svg
      ref={svg}
      className={className ? `nav-cord ${className}` : "nav-cord"}
      aria-hidden="true"
      focusable="false"
    >
      <g transform={`translate(0 10) scale(${SCALE})`}>
        <path className="knot-cord" strokeWidth={6} />
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            <path className="knot-case" strokeWidth={8.8} pathLength={1} />
            <path className="knot-turn" strokeWidth={6.4} pathLength={1} />
          </React.Fragment>
        ))}
      </g>
    </svg>
  );
};

export default NavCord;
